import { useState, useEffect } from 'react'
import { LabResult, LabPartner, LabVerificationRequest } from '../types/labResults'

interface LabResultVerificationProps {
  productId?: string
  vendorId?: string
  onVerificationComplete?: (result: LabResult) => void
}

export default function LabResultVerification({ 
  productId, 
  vendorId, 
  onVerificationComplete 
}: LabResultVerificationProps) {
  const [activeTab, setActiveTab] = useState<'upload' | 'verify' | 'partners' | 'history'>('upload')
  const [labResults, setLabResults] = useState<LabResult[]>([])
  const [labPartners, setLabPartners] = useState<LabPartner[]>([])
  const [verificationRequests, setVerificationRequests] = useState<LabVerificationRequest[]>([])
  const [showUploadModal, setShowUploadModal] = useState(false)

  // Mock data for demonstration
  useEffect(() => {
    // Mock lab partners
    setLabPartners([
      {
        id: 'acs-lab',
        name: 'ACS Laboratory',
        certificationNumber: 'DEA-123456',
        accreditations: ['ISO 17025', 'DEA Certified', 'FDA Registered'],
        contactInfo: {
          phone: '(555) 123-4567',
          email: 'info@acslaboratory.com',
          website: 'https://acslaboratory.com',
          address: '123 Lab Street, Science City, SC 12345'
        },
        specialties: ['Alkaloid Testing', 'Heavy Metals', 'Microbials', 'Pesticides'],
        isVerified: true,
        verificationDate: new Date('2024-01-01'),
        isActive: true
      },
      {
        id: 'green-labs',
        name: 'Green Scientific Labs',
        certificationNumber: 'ISO-789012',
        accreditations: ['ISO 17025', 'AOAC Certified'],
        contactInfo: {
          phone: '(555) 987-6543',
          email: 'testing@greenscientific.com',
          website: 'https://greenscientific.com',
          address: '456 Research Blvd, Lab Town, LT 67890'
        },
        specialties: ['Botanical Testing', 'Contamination Screening', 'Potency Analysis'],
        isVerified: true,
        verificationDate: new Date('2024-01-15'),
        isActive: true
      }
    ])

    // Mock lab results
    setLabResults([
      {
        id: 'lab-result-1',
        productId: 'thai-7oh-powder-1',
        vendorId: 'vendor-1',
        batchNumber: 'TH7OH-240115-001',
        testDate: new Date('2024-01-15'),
        labName: 'ACS Laboratory',
        labCertificationNumber: 'DEA-123456',
        alkaloids: {
          mitragynine: 1.2,
          '7ohMitragynine': 6.8,
          paynantheine: 0.3,
          speciogynine: 0.2,
          total: 8.5
        },
        safetyTests: {
          heavyMetals: {
            lead: 0.1,
            mercury: 0.05,
            cadmium: 0.02,
            arsenic: 0.08,
            passed: true
          },
          microbials: {
            salmonella: 'not-detected',
            ecoli: 'not-detected',
            yeastMold: 50,
            totalBacteria: 100,
            passed: true
          },
          pesticides: {
            tested: true,
            detected: [],
            passed: true
          },
          residualSolvents: {
            tested: true,
            detected: [],
            passed: true
          }
        },
        verification: {
          status: 'verified',
          verifiedBy: 'admin-1',
          verifiedDate: new Date('2024-01-16'),
          expiryDate: new Date('2024-07-15')
        },
        certificate: {
          originalFileName: 'TH7OH-240115-001-COA.pdf',
          fileUrl: '/certificates/TH7OH-240115-001-COA.pdf',
          uploadDate: new Date('2024-01-15'),
          fileHash: 'sha256:abc123...',
          qrCodeData: 'verify:TH7OH-240115-001:abc123'
        },
        testingMethod: 'HPLC-UV',
        sampleWeight: 5.0,
        moistureContent: 8.2,
        isPublic: true,
        createdDate: new Date('2024-01-15'),
        lastUpdated: new Date('2024-01-16')
      }
    ])
  }, [])

  const tabs = [
    { id: 'upload', label: 'Upload Results', icon: '📤' },
    { id: 'verify', label: 'Verify Results', icon: '✅' },
    { id: 'partners', label: 'Lab Partners', icon: '🏢' },
    { id: 'history', label: 'History', icon: '📋' }
  ]

  return (
    <div className="lab-verification-system">
      <div className="verification-header">
        <h2>Lab Result Verification System</h2>
        <p>Ensuring transparency and safety through verified third-party testing</p>
      </div>

      <div className="verification-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="verification-content">
        {activeTab === 'upload' && (
          <div className="upload-section">
            <div className="upload-info">
              <h3>Upload Lab Results</h3>
              <p>Upload certificates of analysis (COAs) from verified laboratories</p>
            </div>

            <div className="upload-requirements">
              <h4>Requirements for Lab Results:</h4>
              <ul>
                <li>✅ Must be from a verified laboratory partner</li>
                <li>✅ Include alkaloid analysis (MIT, 7OH-MIT, etc.)</li>
                <li>✅ Heavy metals testing (Pb, Hg, Cd, As)</li>
                <li>✅ Microbial contamination screening</li>
                <li>✅ Batch number matching your product</li>
                <li>✅ Test date within 6 months</li>
                <li>✅ Clear, readable PDF format</li>
              </ul>
            </div>

            <div className="upload-form">
              <div className="form-section">
                <h4>Product Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product Name</label>
                    <input type="text" placeholder="Enter product name" />
                  </div>
                  <div className="form-group">
                    <label>Batch Number</label>
                    <input type="text" placeholder="Enter batch number" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Test Date</label>
                    <input type="date" />
                  </div>
                  <div className="form-group">
                    <label>Laboratory</label>
                    <select>
                      <option value="">Select laboratory</option>
                      {labPartners.filter(lab => lab.isVerified).map(lab => (
                        <option key={lab.id} value={lab.id}>{lab.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Certificate Upload</h4>
                <div className="file-upload-area">
                  <div className="upload-dropzone">
                    <div className="upload-icon">📄</div>
                    <div className="upload-text">
                      <p>Drop your COA file here or click to browse</p>
                      <span>PDF files only, max 10MB</span>
                    </div>
                    <input type="file" accept=".pdf" hidden />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button className="submit-btn">Submit for Verification</button>
                <button className="cancel-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'verify' && (
          <div className="verify-section">
            <div className="verification-queue">
              <h3>Pending Verification</h3>
              <div className="verification-items">
                <div className="verification-item">
                  <div className="item-info">
                    <h4>Thai 7OH Powder - Batch TH240120</h4>
                    <p>Submitted by Elite Extracts • 2 days ago</p>
                    <span className="priority-badge medium">Medium Priority</span>
                  </div>
                  <div className="item-actions">
                    <button className="verify-btn">Start Verification</button>
                    <button className="reject-btn">Reject</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="verified-results">
              <h3>Recently Verified</h3>
              <div className="results-grid">
                {labResults.filter(result => result.verification.status === 'verified').map(result => (
                  <LabResultCard key={result.id} result={result} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'partners' && (
          <div className="partners-section">
            <div className="partners-header">
              <h3>Verified Laboratory Partners</h3>
              <button className="add-partner-btn">+ Add New Partner</button>
            </div>

            <div className="partners-grid">
              {labPartners.map(partner => (
                <div key={partner.id} className="partner-card">
                  <div className="partner-header">
                    <h4>{partner.name}</h4>
                    <span className={`status-badge ${partner.isVerified ? 'verified' : 'pending'}`}>
                      {partner.isVerified ? '✅ Verified' : '⏳ Pending'}
                    </span>
                  </div>

                  <div className="partner-info">
                    <div className="info-item">
                      <strong>Certification:</strong> {partner.certificationNumber}
                    </div>
                    <div className="info-item">
                      <strong>Accreditations:</strong>
                      <div className="accreditation-tags">
                        {partner.accreditations.map(acc => (
                          <span key={acc} className="accreditation-tag">{acc}</span>
                        ))}
                      </div>
                    </div>
                    <div className="info-item">
                      <strong>Specialties:</strong>
                      <div className="specialty-tags">
                        {partner.specialties.map(spec => (
                          <span key={spec} className="specialty-tag">{spec}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="partner-contact">
                    <div className="contact-item">
                      <span className="contact-icon">📞</span>
                      {partner.contactInfo.phone}
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">📧</span>
                      {partner.contactInfo.email}
                    </div>
                    <div className="contact-item">
                      <span className="contact-icon">🌐</span>
                      <a href={partner.contactInfo.website} target="_blank" rel="noopener noreferrer">
                        Website
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-section">
            <div className="history-filters">
              <div className="filter-group">
                <label>Filter by Status:</label>
                <select>
                  <option value="all">All Results</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Date Range:</label>
                <input type="date" />
                <span>to</span>
                <input type="date" />
              </div>
            </div>

            <div className="history-list">
              {labResults.map(result => (
                <div key={result.id} className="history-item">
                  <div className="history-info">
                    <h4>Batch {result.batchNumber}</h4>
                    <p>{result.labName} • {result.testDate.toLocaleDateString()}</p>
                  </div>
                  <div className="history-status">
                    <span className={`status-badge ${result.verification.status}`}>
                      {result.verification.status}
                    </span>
                  </div>
                  <div className="history-actions">
                    <button className="view-btn">View Details</button>
                    <button className="download-btn">Download COA</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .lab-verification-system {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          background: #f5f7fa;
          min-height: 100vh;
        }

        .verification-header {
          background: #2d3748;
          border-radius: 12px;
          padding: 30px;
          margin-bottom: 30px;
          text-align: center;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }

        .verification-header h2 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .verification-nav {
          background: #2d3748;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          gap: 10px;
          overflow-x: auto;
        }

        .nav-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: none;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #a0aec0;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .nav-tab:hover {
          background: #f0f0f0;
          color: #2c5530;
        }

        .nav-tab.active {
          background: #2c5530;
          color: white;
        }

        .tab-icon {
          font-size: 16px;
        }

        .verification-content {
          background: #2d3748;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          min-height: 600px;
        }

        .upload-info {
          margin-bottom: 30px;
        }

        .upload-info h3 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .upload-requirements {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          border-left: 4px solid #4caf50;
        }

        .upload-requirements h4 {
          color: #2c5530;
          margin-bottom: 15px;
        }

        .upload-requirements ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .upload-requirements li {
          padding: 5px 0;
          color: #e2e8f0;
        }

        .form-section {
          margin-bottom: 30px;
        }

        .form-section h4 {
          color: #2c5530;
          margin-bottom: 15px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 15px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .form-group label {
          font-weight: 600;
          color: #e2e8f0;
        }

        .form-group input,
        .form-group select {
          padding: 10px;
          border: 1px solid #4a5568;
          border-radius: 4px;
        }

        .file-upload-area {
          margin-top: 15px;
        }

        .upload-dropzone {
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          background: #fafafa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .upload-dropzone:hover {
          border-color: #2c5530;
          background: #f8fdf8;
        }

        .upload-icon {
          font-size: 3em;
          margin-bottom: 15px;
        }

        .upload-text p {
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 5px;
        }

        .upload-text span {
          color: #a0aec0;
          font-size: 12px;
        }

        .form-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 30px;
        }

        .submit-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
        }

        .cancel-btn {
          background: #f5f5f5;
          color: #a0aec0;
          border: 1px solid #4a5568;
          padding: 12px 30px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
        }

        .verification-queue {
          margin-bottom: 40px;
        }

        .verification-queue h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .verification-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border: 1px solid #4a5568;
          border-radius: 8px;
          margin-bottom: 15px;
        }

        .item-info h4 {
          color: #e2e8f0;
          margin-bottom: 5px;
        }

        .item-info p {
          color: #a0aec0;
          font-size: 14px;
          margin-bottom: 10px;
        }

        .priority-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .priority-badge.medium {
          background: #2d37483cd;
          color: #856404;
        }

        .item-actions {
          display: flex;
          gap: 10px;
        }

        .verify-btn {
          background: #4caf50;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }

        .reject-btn {
          background: #f44336;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }

        .partners-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .partners-header h3 {
          color: #2c5530;
          margin: 0;
        }

        .add-partner-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        }

        .partners-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 20px;
        }

        .partner-card {
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 20px;
          background: #2d3748;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .partner-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .partner-header h4 {
          color: #2c5530;
          margin: 0;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .status-badge.verified {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status-badge.pending {
          background: #2d37483cd;
          color: #856404;
        }

        .partner-info {
          margin-bottom: 15px;
        }

        .info-item {
          margin-bottom: 10px;
          font-size: 14px;
        }

        .info-item strong {
          color: #e2e8f0;
        }

        .accreditation-tags,
        .specialty-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 5px;
        }

        .accreditation-tag {
          background: #e3f2fd;
          color: #1976d2;
          padding: 2px 6px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
        }

        .specialty-tag {
          background: #f3e5f5;
          color: #7b1fa2;
          padding: 2px 6px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 500;
        }

        .partner-contact {
          border-top: 1px solid #eee;
          padding-top: 15px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 12px;
          color: #a0aec0;
        }

        .contact-icon {
          font-size: 14px;
        }

        .contact-item a {
          color: #2c5530;
          text-decoration: none;
        }

        .history-filters {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          align-items: end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .filter-group label {
          font-weight: 600;
          color: #e2e8f0;
          font-size: 14px;
        }

        .filter-group select,
        .filter-group input {
          padding: 8px;
          border: 1px solid #4a5568;
          border-radius: 4px;
        }

        .history-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .history-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border: 1px solid #4a5568;
          border-radius: 8px;
          background: #2d3748;
        }

        .history-info h4 {
          color: #2c5530;
          margin-bottom: 5px;
        }

        .history-info p {
          color: #a0aec0;
          font-size: 14px;
          margin: 0;
        }

        .history-actions {
          display: flex;
          gap: 10px;
        }

        .view-btn,
        .download-btn {
          padding: 6px 12px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          background: #2d3748;
          cursor: pointer;
          font-size: 12px;
        }

        .view-btn:hover,
        .download-btn:hover {
          background: #f5f5f5;
        }

        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }

          .partners-grid {
            grid-template-columns: 1fr;
          }

          .history-item {
            flex-direction: column;
            align-items: stretch;
            gap: 15px;
          }

          .history-actions {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}

function LabResultCard({ result }: { result: LabResult }) {
  const allTestsPassed = result.safetyTests.heavyMetals.passed && 
                        result.safetyTests.microbials.passed && 
                        result.safetyTests.pesticides.passed && 
                        result.safetyTests.residualSolvents.passed

  return (
    <div className="lab-result-card">
      <div className="result-header">
        <h4>Batch {result.batchNumber}</h4>
        <span className={`verification-badge ${result.verification.status}`}>
          {result.verification.status === 'verified' ? '✅ Verified' : '⏳ Pending'}
        </span>
      </div>

      <div className="result-info">
        <div className="test-date">
          <strong>Test Date:</strong> {result.testDate.toLocaleDateString()}
        </div>
        <div className="lab-name">
          <strong>Laboratory:</strong> {result.labName}
        </div>
      </div>

      <div className="alkaloid-results">
        <h5>Alkaloid Content</h5>
        <div className="alkaloid-grid">
          <div className="alkaloid-item">
            <span className="alkaloid-label">7OH-MIT:</span>
            <span className="alkaloid-value">{result.alkaloids['7ohMitragynine']}%</span>
          </div>
          <div className="alkaloid-item">
            <span className="alkaloid-label">MIT:</span>
            <span className="alkaloid-value">{result.alkaloids.mitragynine}%</span>
          </div>
          <div className="alkaloid-item">
            <span className="alkaloid-label">Total:</span>
            <span className="alkaloid-value">{result.alkaloids.total}%</span>
          </div>
        </div>
      </div>

      <div className="safety-results">
        <h5>Safety Tests</h5>
        <div className="safety-status">
          <span className={`safety-badge ${allTestsPassed ? 'passed' : 'failed'}`}>
            {allTestsPassed ? '✅ All Tests Passed' : '❌ Issues Detected'}
          </span>
        </div>
      </div>

      <div className="result-actions">
        <button className="view-details-btn">View Full Report</button>
        <button className="download-coa-btn">Download COA</button>
      </div>

      <style>{`
        .lab-result-card {
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 20px;
          background: #2d3748;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .result-header h4 {
          color: #2c5530;
          margin: 0;
        }

        .verification-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .verification-badge.verified {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .verification-badge.pending {
          background: #2d37483cd;
          color: #856404;
        }

        .result-info {
          margin-bottom: 15px;
          font-size: 14px;
        }

        .result-info div {
          margin-bottom: 5px;
        }

        .alkaloid-results {
          margin-bottom: 15px;
        }

        .alkaloid-results h5 {
          color: #2c5530;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .alkaloid-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .alkaloid-item {
          background: #f8f9fa;
          padding: 8px;
          border-radius: 4px;
          text-align: center;
          font-size: 12px;
        }

        .alkaloid-label {
          display: block;
          color: #a0aec0;
          margin-bottom: 2px;
        }

        .alkaloid-value {
          display: block;
          font-weight: bold;
          color: #2c5530;
        }

        .safety-results {
          margin-bottom: 15px;
        }

        .safety-results h5 {
          color: #2c5530;
          margin-bottom: 10px;
          font-size: 14px;
        }

        .safety-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .safety-badge.passed {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .safety-badge.failed {
          background: #ffebee;
          color: #c62828;
        }

        .result-actions {
          display: flex;
          gap: 10px;
        }

        .view-details-btn,
        .download-coa-btn {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          background: #2d3748;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
        }

        .view-details-btn:hover,
        .download-coa-btn:hover {
          background: #f5f5f5;
        }

        .view-details-btn {
          color: #2c5530;
          border-color: #2c5530;
        }

        .download-coa-btn {
          color: #a0aec0;
        }
      `}</style>
    </div>
  )
}